/* ============================================================================
   dataJsonNormalize — reparación DETERMINISTA de un data.json generado.

   El agente 3 a veces inventa estructuras que el runtime no puede ejecutar
   (visto en producción con "REMARKS CORPORATIVOS 5"):
     · "emisores ocultos" con campoForm = "a,b,c" (lista con comas) → el runtime
       hace data[campoForm] → undefined → NUNCA emite
     · block inexistente (99) · masks de espacios (" ") · skip:true en inputs
       visibles con label
   En vez de rezar para que el agente no lo haga, esto lo REPARA en el acto y
   deja el data.json en la forma canónica: la máscara vive EN el campo que
   dispara la emisión (multi-placeholder permitido).

   normalizeDataJson(dataJson) → { data, changes: [str] }  (no muta el original)
   ============================================================================ */

import { normalizeRemarkField, resolveRemarkSemantics } from './remarkSemantics'

const VALID_BLOCKS = [3, 5, 6, 7, 9]
const INPUT_TYPES = ['text', 'number', 'select', 'date', 'checkbox', 'check', 'mask']
const TRANSACTION_CLOSE_COMMAND_RX = /^\s*(ER|ET|E|IR|I)\s*$/i
const NO_AUTO_CLOSE_PATTERNS = [
  /\bno\s+(?:agregues|agregar|anadas|añadas|incluyas|pongas|poner|ejecutes|ejecutar|uses|usar)\s+(?:el\s+)?(?:comando\s+)?(?:ER|ET|E|IR|I)\b/i,
  /\b(?:sin|no\s+usar|no\s+utilizar|no\s+incluyas|no\s+incluya)\s+(?:ER|ET|E|IR|I)\b/i,
  /\bno\s+(?:guardes|guardar|grabar|grabes|cierres|cerrar|persistas|persistir)\b.{0,80}\b(?:con\s+)?(?:ER|ET|E|end\s+transaction)\b/i,
  /\bno\s+(?:guardes|guardar|grabar|grabes|persistas|persistir)\b.{0,80}\b(?:datos|cambios|remarks|comandos)\b/i,
  /\bno\s+(?:guardes|guardar|grabar|grabes|cierres|cerrar|persistas|persistir)\b.{0,80}\b(?:reserva|pnr|transacci[oó]n|datos|cambios)\b/i,
  /\b(?:deja|dejar|deje|mantener|mantenga)\b.{0,80}\b(?:reserva|pnr|transacci[oó]n)\b.{0,40}\b(?:abierta|sin\s+guardar|sin\s+cerrar)\b/i,
  /\b(?:el\s+)?(?:usuario|vendedor|agente)\b.{0,80}\b(?:decide|decidir[aá]|guardar[aá]|cerrar[aá])\b/i,
  /\b(?:yo|usuario|vendedor|agente)\s+(?:lo\s+)?(?:guardar[eé]|cerrar[eé]|decidir[eé])\b/i,
]

function norm(k) {
  return String(k == null ? '' : k).trim().toLowerCase()
}

function placeholders(text) {
  const out = []
  let m
  const rx = /\[([A-Za-z_][A-Za-z0-9_]*)\]/g
  const s = String(text == null ? '' : text)
  while ((m = rx.exec(s))) out.push(m[1])
  return out
}

function hasNoAutoCloseIntent(text) {
  const value = String(text || '')
  return NO_AUTO_CLOSE_PATTERNS.some((pattern) => pattern.test(value))
}

function stripTransactionCloseCommands(value, changes, path = 'root') {
  if (Array.isArray(value)) {
    const next = []
    value.forEach((item, index) => {
      const clean = stripTransactionCloseCommands(item, changes, path + '[' + index + ']')
      if (clean === undefined) return
      next.push(clean)
    })
    return next
  }
  if (!value || typeof value !== 'object') {
    if (typeof value === 'string' && TRANSACTION_CLOSE_COMMAND_RX.test(value)) {
      changes.push('cierre transaccional removido por instruccion del usuario en ' + path + ': ' + value.trim().toUpperCase())
      return undefined
    }
    return value
  }
  const commandText = value.cmd || value.command || value.text
  if (typeof commandText === 'string' && TRANSACTION_CLOSE_COMMAND_RX.test(commandText)) {
    changes.push('cierre transaccional removido por instruccion del usuario en ' + path + ': ' + commandText.trim().toUpperCase())
    return undefined
  }
  const next = { ...value }
  ;['commands', 'then', 'else', 'do'].forEach((key) => {
    if (Array.isArray(next[key])) {
      next[key] = stripTransactionCloseCommands(next[key], changes, path + '.' + key)
    }
  })
  return next
}

export function applyTransactionIntent(input, userText) {
  const changes = []
  let data
  try {
    data = JSON.parse(JSON.stringify(input))
  } catch (_) {
    return { data: input, changes }
  }
  if (!data || typeof data !== 'object') return { data, changes }
  if (!hasNoAutoCloseIntent(userText)) return { data, changes }

  if (data.autoCloseTransaction !== false) {
    data.autoCloseTransaction = false
    changes.push('autoCloseTransaction:false por instruccion del usuario')
  }
  if (!data.source_additional || typeof data.source_additional !== 'object' || Array.isArray(data.source_additional)) {
    data.source_additional = {}
  }
  if (data.source_additional.autoCloseTransaction !== false) {
    data.source_additional.autoCloseTransaction = false
  }
  data.postCommands = stripTransactionCloseCommands(data.postCommands || [], changes, 'postCommands')
  if (Array.isArray(data.actions)) {
    data.actions = data.actions.map((action, index) => {
      if (!action || typeof action !== 'object') return action
      return {
        ...action,
        commands: stripTransactionCloseCommands(action.commands || [], changes, 'actions[' + index + '].commands'),
      }
    })
  }
  if (!Array.isArray(data.warnings)) data.warnings = []
  data.warnings.push('TRANSACTION_CLOSE_DISABLED_BY_USER: el usuario pidio no guardar/cerrar automaticamente; no se agrega ER/ET/E/IR/I.')
  return { data, changes }
}

export function normalizeDataJson(input) {
  const changes = []
  let data
  try {
    data = JSON.parse(JSON.stringify(input))
  } catch (_) {
    return { data: input, changes: [] }
  }
  if (!data || !Array.isArray(data.forms)) return { data, changes }

  data.forms.forEach((form, fi) => {
    if (!form || !Array.isArray(form.remarks)) return
    const fields = form.remarks
    const keyOf = (f) => norm(f && (f.tag || f.campoForm))
    const byKey = new Map()
    fields.forEach((f) => {
      ;[f.tag, f.campoForm].map(norm).filter(Boolean).forEach((k) => { if (!byKey.has(k)) byKey.set(k, f) })
    })

    // --- R1: masks de solo espacios → '' (el runtime emitía "5 VALOR" basura) ---
    fields.forEach((f) => {
      ;['remark', 'remark_mask'].forEach((prop) => {
        if (f && typeof f[prop] === 'string' && f[prop] !== '' && !f[prop].trim()) {
          f[prop] = ''
          changes.push('mask de espacios vaciada en "' + (f.tag || f.label) + '"')
        }
      })
    })

    // --- R2: campoForm con comas → detectar emisor compuesto y FUSIONARLO ---
    // El mask del emisor se muda al campo "primario" (el placeholder cuyo campo
    // no tiene mask propia — típicamente la línea N) y el emisor se elimina.
    const toRemove = new Set()
    fields.forEach((f) => {
      const cf = String((f && f.campoForm) || '')
      const isCompositeKey = cf.indexOf(',') >= 0
      const mask = String((f && (f.remark_mask || f.remark)) || '').trim()
      const phs = placeholders(mask)
      const isHiddenEmitter = (f.visible === false || !String(f.label || '').trim()) && phs.length > 0
      if (!isCompositeKey && !isHiddenEmitter) return
      if (!mask || !phs.length) {
        if (isCompositeKey) {
          f.campoForm = f.tag || cf.split(',')[0].trim()
          changes.push('campoForm con comas corregido en "' + (f.tag || cf) + '"')
        }
        return
      }
      // primario: el ÚLTIMO placeholder que corresponda a un campo real sin mask propia
      let primary = null
      for (let i = phs.length - 1; i >= 0; i--) {
        const cand = byKey.get(norm(phs[i]))
        if (cand && cand !== f && !String((cand.remark_mask || cand.remark) || '').trim()) { primary = cand; break }
      }
      if (!primary) {
        for (let i = phs.length - 1; i >= 0; i--) {
          const cand = byKey.get(norm(phs[i]))
          if (cand && cand !== f) { primary = cand; break }
        }
      }
      if (primary) {
        primary.remark_mask = mask
        primary.remark = mask
        const fb = Number(f.block)
        const pb = Number(primary.block)
        if (VALID_BLOCKS.indexOf(pb) < 0) primary.block = VALID_BLOCKS.indexOf(fb) >= 0 ? fb : 5
        toRemove.add(f)
        changes.push('emisor oculto "' + (f.tag || cf) + '" fusionado: su mask ahora vive en "' + (primary.tag || primary.campoForm) + '"')
      } else if (isCompositeKey) {
        f.campoForm = f.tag || cf.split(',')[0].trim()
        changes.push('campoForm con comas corregido en "' + (f.tag || cf) + '"')
      }
    })
    if (toRemove.size) {
      form.remarks = fields.filter((f) => !toRemove.has(f))
    }

    // --- R3: block inválido → 5 ---
    form.remarks.forEach((f) => {
      const b = Number(f && f.block)
      if (f && f.block !== undefined && f.block !== '' && VALID_BLOCKS.indexOf(b) < 0) {
        f.block = 5
        changes.push('block inválido (' + b + ') → 5 en "' + (f.tag || f.label) + '"')
      }
    })

    // --- R3b: emisor sin block → inferirlo del prefijo del mask ---
    // (visto en producción: DOCS/CTCE/CTCM sin block → Sabre recibió "DOCS/..." inválido)
    form.remarks.forEach((f) => {
      if (!f) return
      const mask = String((f.remark_mask || f.remark) || '').trim()
      if (!mask) return
      const b = Number(f.block)
      const hasBlock = f.block !== undefined && f.block !== '' && !Number.isNaN(b)
      if (hasBlock) return
      const probe = mask.replace(/\[[^\]]+\]/g, 'X').toUpperCase()
      let inferred = null
      if (/^(DOCS|DOCO|DOCA|CTCE|CTCM|CTCR|OSI |OSI\/|SSR )/.test(probe)) inferred = 3
      else if (/^TAW/.test(probe)) inferred = 7
      else if (/^(H-|\.|-|\*|H\/|\/)/.test(probe)) inferred = 5
      else if (!/^([*]|[0-9])/.test(probe)) inferred = 5
      if (inferred !== null) {
        f.block = inferred
        changes.push('emisor "' + (f.tag || f.label) + '" sin block → block ' + inferred + ' inferido del mask')
      }
    })

    // --- R3c: alinear el tipo semántico con el comando Sabre real ---
    // forms[].remarks[] también aloja SSR, phone, received, ticketing y PE.
    // Nunca convertir PE¥ en un remark 5; block 3 siempre representa SSR/OSI.
    form.remarks = form.remarks.map((f) => {
      if (!f) return f
      const beforeType = String(f.typeremark || '')
      const beforeBlock = f.block
      const semantic = resolveRemarkSemantics(f)
      const next = normalizeRemarkField(f)
      if (String(next.typeremark || '') !== beforeType || String(next.block ?? '') !== String(beforeBlock ?? '')) {
        const target = semantic.kind === 'pe' ? 'comando directo PE (sin block)' : semantic.typeremark + ' / block ' + semantic.block
        changes.push('semántica de "' + (f.tag || f.label) + '" corregida a ' + target)
      }
      return next
    })

    // --- R4: skip coherente con el rol del campo ---
    // Semántica REAL del runtime (app-v2 línea ~3309): skip:true + mask vacía =
    // "no te emitas solo" (el campo se usa como sub-placeholder de otro mask).
    //   · campo CON mask (emisor)  → skip:false (debe emitir)
    //   · campo SIN mask pero referenciado en el mask de otro → skip:true
    //     (sin esto el runtime emite basura tipo "5H-" o "5CENTRO COSTO")
    const referenced = new Set()
    form.remarks.forEach((f) => {
      placeholders(String((f && (f.remark_mask || f.remark)) || '')).forEach((ph) => referenced.add(norm(ph)))
    })
    form.remarks.forEach((f) => {
      if (!f || INPUT_TYPES.indexOf(norm(f.typedata)) < 0) return
      const ownMask = String((f.remark_mask || f.remark) || '').trim()
      const isReferenced = [f.tag, f.campoForm].map(norm).some((k) => k && referenced.has(k))
      if (ownMask && f.skip === true) {
        f.skip = false
        changes.push('emisor "' + (f.tag || f.label) + '": skip:false (tiene mask, debe emitir)')
      } else if (!ownMask && isReferenced && f.skip !== true) {
        f.skip = true
        changes.push('input "' + (f.tag || f.label) + '": skip:true (solo alimenta otro mask, no se auto-emite)')
      }
    })

    // --- R5: campoForm !== tag → igualar (schema canónico) ---
    form.remarks.forEach((f) => {
      if (f && f.tag && f.campoForm && String(f.campoForm).indexOf(',') < 0 && norm(f.tag) !== norm(f.campoForm)) {
        f.campoForm = f.tag
        changes.push('campoForm igualado a tag en "' + f.tag + '"')
      }
    })

    // --- R6: reindexar ---
    form.remarks.forEach((f, i) => { if (f) f.index = i })
  })

  return { data, changes }
}
